import React from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	Tooltip,
	Chip,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LoadingBackdrop from 'components/LoadingBackdrop';
import useCaseForm from 'containers/pages/cases/hooks/useCaseForm';
import MissingFieldsHint from 'containers/pages/cases/components/caseForm/MissingFieldsHint';
import CaseFormFields from 'containers/pages/cases/components/caseForm/CaseFormFields';

interface CaseFormDialogProps {
	open: boolean;
	onClose: () => void;
	mode: 'add' | 'edit' | 'copy';
	caseId?: number;
	onSave: () => void;
	onGPDChanged?: (payload: {
		caseId: number;
		caseTitle: string;
		groupId: number;
		groupTitle: string;
		projectId: number;
		projectTitle: string;
		deliverableId: number;
		deliverableTitle: string;
		casePayload: Record<string, unknown>;
	}) => void;
}

const modeConfig: Record<string, { label: string; icon: React.ReactElement; color: 'success' | 'info' | 'warning' }> = {
	add: { label: 'New Case', icon: <AddIcon sx={{ fontSize: '0.875rem' }} />, color: 'success' },
	edit: { label: 'Edit Case', icon: <EditIcon sx={{ fontSize: '0.875rem' }} />, color: 'info' },
	copy: { label: 'Duplicate Case', icon: <ContentCopyIcon sx={{ fontSize: '0.875rem' }} />, color: 'warning' },
};

const CaseFormDialog: React.FC<CaseFormDialogProps> = ({
	open,
	onClose,
	mode,
	caseId,
	onSave,
	onGPDChanged,
}) => {
	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
	const { state, handler } = useCaseForm({ mode, caseId, onSave, onClose, onGPDChanged });
	const config = modeConfig[mode];

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			fullScreen={fullScreen}
			PaperProps={{
				sx: { maxHeight: fullScreen ? '100%' : '90vh' },
			}}
		>
			<DialogTitle
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 1.5,
					py: 1.5,
					borderBottom: 1,
					borderColor: 'divider',
				}}
			>
				<Chip
					icon={config.icon}
					label={config.label}
					color={config.color}
					size="small"
					variant="outlined"
					sx={{ fontWeight: 600 }}
				/>
				{mode === 'edit' && caseId && (
					<Chip
						label={`#${caseId}`}
						size="small"
						variant="outlined"
						sx={{ fontWeight: 500, color: 'text.secondary' }}
					/>
				)}
			</DialogTitle>
			<DialogContent sx={{ py: 2.5, px: { xs: 2, sm: 3 } }}>
				<Box sx={{ pt: 1 }}>
					<CaseFormFields
						form={state.form}
						valid={state.valid}
						mode={state.mode}
						handler={handler}
					/>
				</Box>
			</DialogContent>
			<DialogActions
				sx={{
					flexDirection: 'column',
					alignItems: 'stretch',
					px: { xs: 2, sm: 3 },
					py: 1.5,
					gap: 1,
					borderTop: 1,
					borderColor: 'divider',
				}}
			>
				<MissingFieldsHint canNotSave={state.canNotSave} valid={state.valid} />
				<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
					<Button variant="outlined" color="inherit" onClick={onClose} size="small">
						Cancel
					</Button>
					<Button variant="outlined" onClick={handler.onReset} size="small">
						Reset
					</Button>
					<Tooltip
						title={state.canNotSave ? 'Please fill in all required fields' : ''}
						placement="top"
					>
						<span>
							<Button
								variant="contained"
								disabled={state.canNotSave}
								onClick={handler.onSave}
								size="small"
							>
								{mode === 'edit' ? 'Save' : 'Create'}
							</Button>
						</span>
					</Tooltip>
				</Box>
			</DialogActions>
			<LoadingBackdrop open={state.isLoading} />
		</Dialog>
	);
};

export default CaseFormDialog;
